import { useRef, useState } from 'react'
import {
  useDeleteVersion,
  usePromoteDraft,
  useRollback,
  useUploadDraft,
  useVersions,
} from '../../hooks/useVersions'
import { Card } from '../../components/ui/Card'
import { Spinner } from '../../components/ui/Spinner'
import { Badge } from '../../components/Badge'
import { Button, buttonClasses } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import {
  ResponsiveTable,
  type Column,
} from '../../components/ui/ResponsiveTable'
import { ErrorBanner } from '../../components/ErrorBanner'
import { ApiError, apiErrorMessage } from '../../api/client'
import { glowVersionUrl } from '../../lib/glowUrl'
import { formatBytes } from '../../lib/format'
import { useMe } from '../../hooks/useMe'
import type { Glow, Version } from '../../api/types'

// Per-row action the user is about to confirm. The same dialog renders
// the prompt + verb + busy/error state regardless of action, keeping
// the modal pattern aligned across rollback / promote / delete.
type ConfirmAction =
  | { kind: 'rollback'; version: Version }
  | { kind: 'promote'; version: Version }
  | { kind: 'delete'; version: Version }

// VersionsTab shows deploy history, the "Upload to Versions" entry
// point for Draft uploads, and per-row promote / delete / rollback
// actions. CLI/agent tooling still owns direct-to-Live deploys via
// glow-api with an API key (see API Keys tab); the console upload
// path always lands as a Draft so the owner can preview before
// flipping the live pointer.
export function VersionsTab({ glow }: { glow: Glow }) {
  const { data: versions, isLoading, error } = useVersions(glow.id)
  const { data: me } = useMe()
  const currentUid = me?.uid ?? null
  const rollback = useRollback(glow.id)
  const promote = usePromoteDraft(glow.id)
  const remove = useDeleteVersion(glow.id)
  const upload = useUploadDraft(glow.id)

  const [confirm, setConfirm] = useState<ConfirmAction | null>(null)
  const [confirmError, setConfirmError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [showUpload, setShowUpload] = useState(false)

  const anyPending =
    rollback.isPending || promote.isPending || remove.isPending

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }
  if (error) {
    return <ErrorBanner>{(error as Error).message}</ErrorBanner>
  }

  const doConfirmed = async (a: ConfirmAction) => {
    setConfirmError(null)
    try {
      if (a.kind === 'rollback') await rollback.mutateAsync(a.version.id)
      else if (a.kind === 'promote') {
        const res = await promote.mutateAsync()
        const n = res.deleted_version_ids?.length ?? 0
        setNotice(
          n > 0
            ? `Promoted. Removed ${n} intermediate Draft${n === 1 ? '' : 's'}.`
            : 'Promoted to Live.',
        )
      } else await remove.mutateAsync(a.version.id)
      setConfirm(null)
    } catch (e) {
      setConfirmError(apiErrorMessage(e, 'Action failed.'))
    }
  }

  const empty = !versions || versions.length === 0

  // Columns for the deploy-history table. Declared here so the cells can
  // close over `currentUid` (uploaderLabel) and `glowName` (the action
  // cluster's draft-preview link). The header strings double as the
  // stacked-card labels below sm.
  const columns: Column<Version>[] = [
    {
      header: 'Deployed',
      cell: (v) => <span title={v.created_at}>{fmtDate(v.created_at)}</span>,
    },
    {
      header: 'Version',
      primary: true,
      cell: (v) => (
        <span className="font-mono text-xs">
          <span>{shortId(v.id)}</span>
          {(v.is_live || v.is_draft) && (
            <span className="ml-2 inline-flex gap-1 align-middle">
              {v.is_live && <Badge tone="green">Live</Badge>}
              {v.is_draft && <Badge tone="amber">Draft</Badge>}
            </span>
          )}
        </span>
      ),
    },
    {
      header: 'Size',
      cell: (v) => <span className="text-cream/70">{formatBytes(v.size)}</span>,
    },
    {
      header: 'Uploader',
      cell: (v) => (
        <span className="font-mono text-xs text-muted">
          {uploaderLabel(v, currentUid)}
        </span>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <Toolbar onUpload={() => setShowUpload(true)} />

      {notice && (
        <div className="flex items-center justify-between rounded border border-glow-600/30 bg-glow-600/10 px-3 py-2 text-sm text-cream/80">
          <span>{notice}</span>
          <button
            type="button"
            className="ml-3 text-muted hover:text-cream"
            onClick={() => setNotice(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {empty ? (
        <Card>
          <h3 className="font-medium">No deploys yet</h3>
          <p className="mt-1 text-sm text-cream/70">
            Click <span className="font-medium">Upload to Versions</span> above
            to push a Draft from your machine — it will be reachable at
            <code className="mx-1 rounded bg-surface-2 px-1 font-mono text-xs">
              /_v/&lt;version-id&gt;/
            </code>
            on your glow until you promote it to Live. Your bundle needs{' '}
            <code className="rounded bg-surface-2 px-1 font-mono text-xs">index.html</code>,{' '}
            <code className="rounded bg-surface-2 px-1 font-mono text-xs">APP.md</code>, and{' '}
            <code className="rounded bg-surface-2 px-1 font-mono text-xs">DATA.md</code>{' '}
            at the archive root. Or use the deploy API with an{' '}
            <a
              className="font-medium text-glow-600 hover:underline"
              href="https://backlit.run/docs"
              target="_blank"
              rel="noreferrer"
            >
              API key
            </a>{' '}
            to push straight to Live.
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <ResponsiveTable
            columns={columns}
            rows={versions}
            rowKey={(v) => v.id}
            actions={(v) => (
              <RowActions
                version={v}
                glowName={glow.name}
                busy={anyPending}
                onRollback={() => setConfirm({ kind: 'rollback', version: v })}
                onPromote={() => setConfirm({ kind: 'promote', version: v })}
                onDelete={() => setConfirm({ kind: 'delete', version: v })}
              />
            )}
          />
        </Card>
      )}

      {confirm && (
        <ConfirmDialog
          action={confirm}
          onCancel={() => {
            setConfirm(null)
            setConfirmError(null)
          }}
          onConfirm={() => doConfirmed(confirm)}
          busy={anyPending}
          error={confirmError}
        />
      )}

      {showUpload && (
        <UploadDialog
          mutation={upload}
          onClose={() => setShowUpload(false)}
        />
      )}
    </div>
  )
}

function Toolbar({ onUpload }: { onUpload: () => void }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="text-sm text-cream/70">
        Drafts upload here; promote one to make it Live.
      </div>
      <Button onClick={onUpload}>Upload to Versions</Button>
    </div>
  )
}

function RowActions({
  version,
  glowName,
  busy,
  onRollback,
  onPromote,
  onDelete,
}: {
  version: Version
  glowName: string
  busy: boolean
  onRollback: () => void
  onPromote: () => void
  onDelete: () => void
}) {
  // Only the live version cannot be deleted (the server refuses it with 409
  // version_in_use). The button is rendered-but-disabled on the live row so
  // the affordance stays discoverable while the constraint is obvious; roll
  // back or promote a different version to enable it. Deleting any other
  // version — including the staged draft — reclaims its bytes from the
  // shared per-glow data cap; deleting the draft also clears the draft
  // pointer server-side (replacing the old "Discard draft" action).
  const deleteDisabled = busy || version.is_live
  const deleteBtn = (
    <Button
      variant="danger"
      onClick={onDelete}
      disabled={deleteDisabled}
      title={
        version.is_live
          ? 'The live version cannot be deleted'
          : 'Delete this version and reclaim its storage'
      }
    >
      Delete
    </Button>
  )

  // Live + Draft simultaneously (a promoted draft): the draft pointer still
  // resolves at /_v/, so offer a preview. Delete stays disabled (it's live).
  if (version.is_live && version.is_draft) {
    return (
      <div className="flex flex-wrap gap-2">
        <ViewDraftLink glowName={glowName} versionID={version.id} />
        {deleteBtn}
      </div>
    )
  }
  // Live only: no pointer verb, but show the (disabled) Delete affordance.
  if (version.is_live) return <div className="flex flex-wrap gap-2">{deleteBtn}</div>
  // Draft only: preview + promote (set Live to draft) + Delete (removes the
  // version and clears the draft pointer, reclaiming its bytes).
  if (version.is_draft) {
    return (
      <div className="flex flex-wrap gap-2">
        <ViewDraftLink glowName={glowName} versionID={version.id} />
        <Button onClick={onPromote} disabled={busy}>
          Promote to Live
        </Button>
        {deleteBtn}
      </div>
    )
  }
  // Neither: existing rollback verb (set Live to this older version) plus
  // the Delete.
  return (
    <div className="flex flex-wrap gap-2">
      <Button variant="secondary" onClick={onRollback} disabled={busy}>
        Roll back
      </Button>
      {deleteBtn}
    </div>
  )
}

// ViewDraftLink opens the draft's immutable /_v/{version}/ URL on the
// glow's own subdomain in a new tab so the owner can preview a staged
// draft before promoting it to Live. Rendered as an <a> styled like a
// secondary Button (a real <button> can't navigate / open a new tab).
function ViewDraftLink({
  glowName,
  versionID,
}: {
  glowName: string
  versionID: string
}) {
  return (
    <a
      href={glowVersionUrl(glowName, versionID)}
      target="_blank"
      rel="noreferrer"
      className={buttonClasses('secondary')}
    >
      View Draft
    </a>
  )
}

function ConfirmDialog({
  action,
  onCancel,
  onConfirm,
  busy,
  error,
}: {
  action: ConfirmAction
  onCancel: () => void
  onConfirm: () => void
  busy: boolean
  error: string | null
}) {
  const meta = confirmMeta(action)
  return (
    <Modal onClose={onCancel} labelledBy="version-confirm-title">
      <h3 id="version-confirm-title" className="text-lg font-semibold">
        {meta.title}
      </h3>
      <p className="mt-2 text-sm text-cream/70">
        {meta.body(shortId(action.version.id))}
      </p>
      {error && <ErrorBanner className="mt-3">{error}</ErrorBanner>}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={onConfirm} disabled={busy}>
          {busy ? meta.busyLabel : meta.confirmLabel}
        </Button>
      </div>
    </Modal>
  )
}

function confirmMeta(action: ConfirmAction): {
  title: string
  body: (versionShortID: string) => React.ReactNode
  confirmLabel: string
  busyLabel: string
} {
  switch (action.kind) {
    case 'rollback':
      return {
        title: 'Roll back to this version?',
        body: (id) => (
          <>
            Visitors will be served version{' '}
            <code className="rounded bg-surface-2 px-1 font-mono text-xs">{id}</code>{' '}
            immediately. You can roll forward again later.
          </>
        ),
        confirmLabel: 'Roll back',
        busyLabel: 'Rolling back…',
      }
    case 'promote':
      return {
        title: 'Promote draft to Live?',
        body: (id) => (
          <>
            Visitors will be served version{' '}
            <code className="rounded bg-surface-2 px-1 font-mono text-xs">{id}</code>{' '}
            immediately. The version stays pinned as the Draft too, so its
            <code className="mx-1 rounded bg-surface-2 px-1 font-mono text-xs">/_v/</code>
            URL keeps working. Any Drafts staged after the current Live version but
            before this one are removed and their storage reclaimed.
          </>
        ),
        confirmLabel: 'Promote to Live',
        busyLabel: 'Promoting…',
      }
    case 'delete':
      return {
        title: 'Delete this version?',
        body: (id) => (
          <>
            Version{' '}
            <code className="rounded bg-surface-2 px-1 font-mono text-xs">{id}</code>{' '}
            and its assets are permanently removed and its storage is
            reclaimed from this glow's data cap. This cannot be undone, and
            its{' '}
            <code className="rounded bg-surface-2 px-1 font-mono text-xs">/_v/</code>{' '}
            URL stops resolving.
          </>
        ),
        confirmLabel: 'Delete version',
        busyLabel: 'Deleting…',
      }
  }
}

function UploadDialog({
  mutation,
  onClose,
}: {
  mutation: ReturnType<typeof useUploadDraft>
  onClose: () => void
}) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [file, setFile] = useState<File | null>(null)
  // ReactNode (not string) so the bundle-rejection branches can carry
  // <code>-formatted file names; ErrorBanner already accepts ReactNode.
  const [uiError, setUiError] = useState<React.ReactNode | null>(null)

  const busy = mutation.isPending

  const onSubmit = async () => {
    setUiError(null)
    if (!file) {
      setUiError('Pick a .tar.gz or .tgz archive first.')
      return
    }
    try {
      await mutation.mutateAsync(file)
      onClose()
    } catch (e) {
      // The two bundle-contract rejections get a friendly framing line, but
      // the server message is ALWAYS shown verbatim — it names the specific
      // missing files / offending files+tokens and must never be replaced.
      if (e instanceof ApiError && e.code === 'missing_required_files') {
        setUiError(
          <>
            <p>
              Your bundle must contain{' '}
              <code className="rounded bg-surface-2 px-1 font-mono text-xs">
                index.html
              </code>
              ,{' '}
              <code className="rounded bg-surface-2 px-1 font-mono text-xs">
                APP.md
              </code>
              , and{' '}
              <code className="rounded bg-surface-2 px-1 font-mono text-xs">
                DATA.md
              </code>{' '}
              at the archive root.
            </p>
            <p className="mt-1">{e.message}</p>
          </>,
        )
        return
      }
      if (e instanceof ApiError && e.code === 'browser_storage_forbidden') {
        setUiError(
          <>
            <p>{e.message}</p>
            <p className="mt-1">
              Glow apps must use the Backlit data APIs (
              <code className="rounded bg-surface-2 px-1 font-mono text-xs">
                backlit.data
              </code>{' '}
              /{' '}
              <code className="rounded bg-surface-2 px-1 font-mono text-xs">
                backlit.userdata
              </code>
              ) instead of browser storage.
            </p>
          </>,
        )
        return
      }
      setUiError(apiErrorMessage(e, 'Upload failed.'))
    }
  }

  return (
    <Modal onClose={onClose} labelledBy="version-upload-title">
      <h3 id="version-upload-title" className="text-lg font-semibold">
        Upload a new Draft
      </h3>
      <p className="mt-2 text-sm text-cream/70">
          Upload a <code className="rounded bg-surface-2 px-1 font-mono text-xs">.tar.gz</code>{' '}
          of your site. The archive must contain{' '}
          <code className="rounded bg-surface-2 px-1 font-mono text-xs">index.html</code>,{' '}
          <code className="rounded bg-surface-2 px-1 font-mono text-xs">APP.md</code>, and{' '}
          <code className="rounded bg-surface-2 px-1 font-mono text-xs">DATA.md</code>{' '}
          at its root, and must not use browser storage APIs — use the Backlit
          SDK instead. The version becomes reachable at{' '}
          <code className="rounded bg-surface-2 px-1 font-mono text-xs">/_v/</code>
          immediately. Promote it to Live when you're ready. Each upload adds a new
          Draft below; promoting one to Live removes the older un-promoted Drafts staged
          before it.
        </p>
        <div className="mt-4">
          <input
            ref={inputRef}
            type="file"
            accept=".tar.gz,.tgz,application/gzip,application/x-gzip"
            disabled={busy}
            onChange={(e) => setFile(e.currentTarget.files?.[0] ?? null)}
            className="block w-full text-sm text-cream/80 file:mr-3 file:rounded file:border-0 file:bg-surface-2 file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-surface-2"
          />
          {file && (
            <p className="mt-2 text-xs text-muted">
              {file.name} — {formatBytes(file.size)}
            </p>
          )}
        </div>
        {uiError && <ErrorBanner className="mt-3">{uiError}</ErrorBanner>}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={busy || !file}>
            {busy ? 'Uploading…' : 'Upload Draft'}
          </Button>
        </div>
    </Modal>
  )
}

// uploaderLabel renders the version's uploader with a fallback chain:
// uploader_user_uid (console / MCP deploys; "you" when it's the signed-in
// owner, else the short uid) → uploader_key_hash (API-key deploys) → "—"
// when neither attribution is present (pre-attribution / legacy rows).
function uploaderLabel(version: Version, currentUid: string | null): string {
  if (version.uploader_user_uid) {
    return version.uploader_user_uid === currentUid
      ? 'you'
      : shortId(version.uploader_user_uid)
  }
  if (version.uploader_key_hash) return shortId(version.uploader_key_hash)
  return '—'
}

function shortId(s: string): string {
  return s.length > 12 ? s.slice(0, 12) : s
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

